// Module registry — defines every optional product/feature a client can enable.
// alwaysOn modules cannot be toggled; they are included in every plan.
// requiredStates: if set, the module only appears when the client operates in one of those states.
// navIds: which sidebar nav IDs belong to this module (filtered from sidebar when off).

import {
  PlaneIcon, SparklesIcon, UserPlusIcon, DollarSignIcon, ShieldCheckIcon,
  AlertTriangleIcon, ZapIcon, TrendingUpIcon, FileBarChart2Icon, FileTextIcon,
} from 'lucide-react';

export const MODULES = [
  {
    id:          'leave_management',
    label:       'Leave Management',
    description: 'FMLA leave requests, certifications, compliance timeline, and HR leave hub',
    icon:        PlaneIcon,
    navIds:      ['leave-hub', 'pending', 'leave-case', 'all-leaves'],
    alwaysOn:    true,
    requiredStates: [],
  },
  {
    id:          'ai_assistant',
    label:       'CertifyAI',
    description: 'AI-powered HR assistant for leave questions, compliance guidance, and document analysis',
    icon:        SparklesIcon,
    navIds:      ['ai-assistant'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'onboarding',
    label:       'Employee Onboarding',
    description: 'Guided onboarding flows, offer letters, I-9, and direct deposit collection',
    icon:        UserPlusIcon,
    navIds:      ['onboarding'],
    alwaysOn:    false,
    // Certify is no longer positioning itself as employee's own HRIS of record for
    // onboarding paperwork (W-4/I-9/offer letters/direct deposit). Deactivated for
    // every client regardless of stored available_modules/enabled_modules settings.
    deprecated:  true,
    requiredStates: [],
  },
  {
    id:          'payroll',
    label:       'Payroll',
    description: 'Payroll overview, wage tracking, and pay history for HR admins',
    icon:        DollarSignIcon,
    navIds:      ['payroll'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'accommodations',
    label:       'ADA / PWFA Accommodations',
    description: 'Track and manage ADA and Pregnant Workers Fairness Act accommodation requests',
    icon:        ShieldCheckIcon,
    navIds:      ['accommodations'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'incident_reports',
    label:       'Incident Reports',
    description: 'HR incident tracking and resolution workflow',
    icon:        AlertTriangleIcon,
    navIds:      ['incident-reports'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'workflows',
    label:       'Workflow Automation',
    description: 'Build automated HR workflows triggered by leave events, dates, or status changes',
    icon:        ZapIcon,
    navIds:      ['workflows'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'reports',
    label:       'Reports & Analytics',
    description: 'Headcount, leave usage, compliance, and custom HR reports',
    icon:        TrendingUpIcon,
    navIds:      ['reports'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'compliance_report',
    label:       'Compliance Audit Report',
    description: 'Exportable FMLA compliance audit report for legal and HR review',
    icon:        FileBarChart2Icon,
    navIds:      ['compliance-report'],
    alwaysOn:    false,
    requiredStates: [],
  },
  {
    id:          'famli_wage_report',
    label:       'CO FAMLI Wage Reports',
    description: 'Submit quarterly FAMLI wage reports and premium payments via the My FAMLI+ Employer API',
    icon:        FileTextIcon,
    navIds:      ['famli-wage-report'],
    alwaysOn:    false,
    requiredStates: ['Colorado', 'CO'],
  },
];

// IDs of modules that are always on (cannot be disabled by anyone)
export const ALWAYS_ON_IDS = MODULES.filter((m) => m.alwaysOn).map((m) => m.id);

// IDs of modules that are deprecated (forced off for every client, regardless of settings)
export const DEPRECATED_IDS = MODULES.filter((m) => m.deprecated).map((m) => m.id);

// Module IDs that are on by default when a new client is created
export const DEFAULT_AVAILABLE = [
  'leave_management',
  'ai_assistant',
  'payroll',
  'accommodations',
  'incident_reports',
  'workflows',
  'reports',
  'compliance_report',
  'famli_wage_report',
];

export const DEFAULT_ENABLED = [
  'leave_management',
  'ai_assistant',
  'reports',
];

export function getModule(id) {
  return MODULES.find((m) => m.id === id) ?? null;
}

/**
 * Given a client's operating_states array and available/enabled module lists,
 * compute which modules are actually usable (state-gated + in enabled list).
 */
export function computeEffectiveModules({ operatingStates = [], availableModules, enabledModules }) {
  const available = availableModules ?? DEFAULT_AVAILABLE;
  const enabled   = enabledModules   ?? DEFAULT_ENABLED;

  return MODULES.filter((mod) => {
    if (mod.deprecated) return false;
    if (mod.alwaysOn) return true;
    if (!available.includes(mod.id)) return false;
    if (!enabled.includes(mod.id)) return false;
    if (mod.requiredStates.length > 0) {
      const hasState = mod.requiredStates.some((s) =>
        operatingStates.some((os) => os.toLowerCase().includes(s.toLowerCase()) || s.toLowerCase().includes(os.toLowerCase()))
      );
      if (!hasState) return false;
    }
    return true;
  }).map((m) => m.id);
}
